if (!customElements.get('gift-addons')) {
  customElements.define(
    'gift-addons',
    class GiftAddons extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;
        this.productInfo = this.closest('product-info');
        this.form =
          this.closest('form') ||
          (this.dataset.productFormId ? document.getElementById(this.dataset.productFormId) : null) ||
          this.productInfo?.querySelector('form[data-type="add-to-cart-form"]');
        this.portalledModals = [];
        this.onModalChange = this.onModalChange.bind(this);
        this.onModalClick = this.onModalClick.bind(this);
        this.onModalInput = this.onModalInput.bind(this);
        this.onModalKeydown = this.onModalKeydown.bind(this);

        this.initGiftCardModals();
        this.addEventListener('change', this.onChange.bind(this));
        this.addEventListener('click', this.onClick.bind(this));
        this.addEventListener('input', this.onInput.bind(this));
        this.querySelectorAll('[data-addon-type]').forEach((item) => this.updateItem(item));
        this.updateDynamicCheckout();
      }

      disconnectedCallback() {
        this.portalledModals?.forEach((modal) => {
          modal.removeEventListener('change', this.onModalChange);
          modal.removeEventListener('click', this.onModalClick);
          modal.removeEventListener('input', this.onModalInput);
          modal.remove();
        });
        document.removeEventListener('keydown', this.onModalKeydown);
        document.body.classList.remove('overflow-hidden');

        if (typeof removeTrapFocus === 'function') {
          removeTrapFocus();
        }
      }

      initGiftCardModals() {
        this.querySelectorAll('[data-addon-type="gift_card"]').forEach((item) => {
          const modal = item.querySelector('[data-gift-card-modal]');
          if (!modal) return;

          const modalId = modal.id || `GiftCardModalPortal-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
          modal.id = modalId;
          item.dataset.giftCardModalId = modalId;
          modal.giftAddonsOwner = item;
          modal.addEventListener('change', this.onModalChange);
          modal.addEventListener('click', this.onModalClick);
          modal.addEventListener('input', this.onModalInput);
          document.body.appendChild(modal);
          this.portalledModals.push(modal);
        });
      }

      onChange(event) {
        const item = event.target.closest('[data-addon-type]');
        if (!item) return;

        if (event.target.matches('[data-gift-card-variant-radio]')) {
          this.updateGiftCardVariant(item, event.target);
        }

        this.updateItem(item);
        this.updateDynamicCheckout();

        if (event.target.matches('[data-addon-checkbox]')) {
          if (event.target.checked && this.getGiftCardModal(item)) {
            this.openGiftCardModal(item);
          } else if (!event.target.checked) {
            this.closeGiftCardModal(item, { returnFocus: false });
          }
        }
      }

      onModalChange(event) {
        const modal = event.target.closest('[data-gift-card-modal]');
        const item = this.getGiftCardModalOwner(modal);
        if (!item) return;

        if (event.target.matches('[data-gift-card-variant-radio]')) {
          this.updateGiftCardVariant(item, event.target);
        }

        this.updateItem(item);
      }

      onClick(event) {
        const openButton = event.target.closest('[data-gift-card-modal-open]');
        if (openButton) {
          this.openGiftCardModal(openButton.closest('[data-addon-type]'));
          return;
        }

        const closeButton = event.target.closest('[data-gift-card-modal-close]');
        if (closeButton) {
          this.closeGiftCardModal(closeButton.closest('[data-addon-type]'));
        }
      }

      onModalClick(event) {
        const closeButton = event.target.closest('[data-gift-card-modal-close]');
        if (!closeButton) return;

        const modal = closeButton.closest('[data-gift-card-modal]');
        this.closeGiftCardModal(this.getGiftCardModalOwner(modal));
      }

      onInput(event) {
        if (!event.target.matches('[data-gift-message]')) return;

        this.updateMessageCount(event.target);
        if (event.target.value.trim()) this.setMessageError('', event.target.closest('[data-addon-type]'));
      }

      onModalInput(event) {
        if (!event.target.matches('[data-gift-message]')) return;

        const modal = event.target.closest('[data-gift-card-modal]');
        const item = this.getGiftCardModalOwner(modal);

        this.updateMessageCount(event.target);
        if (event.target.value.trim()) this.setMessageError('', item);
      }

      updateItem(item) {
        const checkbox = item.querySelector('[data-addon-checkbox]');
        const checked = Boolean(checkbox?.checked);
        const variant = item.querySelector('[data-addon-variant]');
        const message = this.getGiftCardElement(item, '[data-gift-message]') || item.querySelector('[data-gift-message]');
        const price = item.querySelector('[data-addon-price]');
        const details = item.querySelector('[data-addon-details]');

        if (variant) {
          variant.disabled = !checked;
          if (variant.matches('select')) variant.required = checked;

          const checkedVariant = this.getGiftCardElement(item, '[data-gift-card-variant-radio]:checked');
          if (checkedVariant) this.updateGiftCardVariant(item, checkedVariant);

          const selectedPrice = variant.selectedOptions?.[0]?.dataset.price || variant.dataset.price;
          if (price && selectedPrice) {
            price.textContent = selectedPrice;
          }
        }

        if (details) details.hidden = !checked;
        if (checkbox) checkbox.setAttribute('aria-expanded', checked ? 'true' : 'false');
        item.classList.toggle('is-selected', checked);

        if (message) {
          message.disabled = !checked;
          message.required = checked;

          if (!checked) {
            message.removeAttribute('aria-invalid');
            this.setMessageError('', item);
          }

          this.updateMessageCount(message);
        }
      }

      updateGiftCardVariant(item, radio) {
        if (!item || !radio) return;

        const variant = item.querySelector('[data-addon-variant]');
        const price = item.querySelector('[data-addon-price]');
        const selected = item.querySelector('[data-gift-card-selected-variant]');

        if (variant) {
          variant.value = radio.value;
          variant.dataset.price = radio.dataset.price || '';
        }

        if (price && radio.dataset.price) {
          price.textContent = radio.dataset.price;
        }

        if (selected && radio.dataset.title) {
          selected.textContent = radio.dataset.title;
        }
      }

      getGiftCardModal(item) {
        if (!item) return null;

        return item.dataset.giftCardModalId
          ? document.getElementById(item.dataset.giftCardModalId)
          : item.querySelector('[data-gift-card-modal]');
      }

      getGiftCardModalOwner(modal) {
        return modal?.giftAddonsOwner || null;
      }

      getGiftCardElement(item, selector) {
        return this.getGiftCardModal(item)?.querySelector(selector) || item?.querySelector(selector);
      }

      openGiftCardModal(item) {
        const modal = this.getGiftCardModal(item);
        if (!modal) return;

        this.activeGiftCardItem = item;
        this.lastFocusedElement = document.activeElement;
        modal.hidden = false;
        document.body.classList.add('overflow-hidden');
        document.addEventListener('keydown', this.onModalKeydown);

        const dialog = modal.querySelector('[data-gift-card-modal-dialog]');
        const focusTarget = modal.querySelector('[data-gift-card-variant-radio]:checked') || modal.querySelector('[data-gift-message]') || dialog;

        requestAnimationFrame(() => {
          if (typeof trapFocus === 'function' && dialog) {
            trapFocus(dialog, focusTarget);
          } else {
            focusTarget?.focus();
          }
        });
      }

      closeGiftCardModal(item = this.activeGiftCardItem, options = { returnFocus: true }) {
        const modal = this.getGiftCardModal(item);
        if (!modal || modal.hidden) return;

        modal.hidden = true;
        document.removeEventListener('keydown', this.onModalKeydown);
        if (!this.portalledModals.some((portalledModal) => !portalledModal.hidden)) {
          document.body.classList.remove('overflow-hidden');
        }

        if (typeof removeTrapFocus === 'function') {
          removeTrapFocus();
        }

        if (options.returnFocus !== false) {
          const returnTarget = item?.querySelector('[data-gift-card-modal-open]') || this.lastFocusedElement;
          if (returnTarget && typeof returnTarget.focus === 'function') returnTarget.focus();
        }
      }

      onModalKeydown(event) {
        if (event.key !== 'Escape') return;

        event.preventDefault();
        this.closeGiftCardModal();
      }

      updateMessageCount(message) {
        const counter =
          message?.closest('[data-gift-card-modal]')?.querySelector('[data-gift-message-count]') ||
          this.querySelector('[data-gift-message-count]');
        if (!counter || !message) return;

        const template = this.dataset.countTemplate || '__count__/__max__';
        counter.textContent = template
          .replace('__count__', message.value.length)
          .replace('__max__', message.maxLength || '200');
      }

      setMessageError(message, item = null) {
        const error = item
          ? this.getGiftCardElement(item, '[data-gift-message-error]')
          : this.querySelector('[data-gift-message-error]') || this.portalledModals.find((modal) => modal.querySelector('[data-gift-message-error]'))?.querySelector('[data-gift-message-error]');
        if (!error) return;

        error.textContent = message;
        error.hidden = !message;
      }

      updateDynamicCheckout() {
        if (this.dataset.hideDynamicCheckout !== 'true' || !this.form) return;

        const scope = this.productInfo || this.form;
        const shouldHide = Boolean(
          scope.querySelector(
            '[data-addon-checkbox]:checked, [data-personalization-toggle]:checked, [data-personalization-checkbox]:checked'
          )
        );
        this.form.querySelectorAll('.shopify-payment-button, [data-shopify="payment-button"]').forEach((button) => {
          button.hidden = shouldHide;
          button.setAttribute('aria-hidden', shouldHide ? 'true' : 'false');
        });
      }
    }
  );
}
